import { createAsyncThunk } from '@reduxjs/toolkit';

import { DictionaryRecord, ContentCompetency, getNuggetCardBadge } from '@backrs_ui/common-ui';

const PROSPER_URL = process.env.REACT_APP_GROW_URL;

export const fetchDataDictionary = createAsyncThunk(
  'dataDictionary/fetchDataDictionary',
  async (competencies: ContentCompetency[], { rejectWithValue }) => {
    try {
      // as more dictionary terms get added (like nuggets, career paths, etc), add them here
      const competencyDictionary = await getCompetencyDictionary(competencies);

      return competencyDictionary;
    } catch (error) {
      return rejectWithValue(`Failed to fetch content competencies with error: ${error}`);
    }
  },
);

const getCompetencyDictionary = async (
  contentCompetencies: ContentCompetency[],
): Promise<DictionaryRecord> => {
  const additionalDictionary: DictionaryRecord = contentCompetencies.reduce((acc, term) => {
    // Split the competency title into individual keywords
    const keywords = term.title
      .toLowerCase()
      .split(/[/\-&]/)
      .map((k) => k.trim());

    // Include the full title as a key
    keywords.push(term.title.toLowerCase());

    const addToDictionary = (keyTerm: string) => {
      const key = `{{${keyTerm}}}`;

      acc[key] = {
        displayType: {
          type: 'nugget',
          badge: getNuggetCardBadge({ variant: 'competency' }),
          onClickUrl: `${PROSPER_URL}/competency/${term.slug}`,
        },
        values: [
          {
            title: term.title,
            description: term.description,
          },
        ],
      };
    };

    /**
     * For each keyword, add it to the dictionary and also add a version of it with the last character removed if it's an S or ING
     *
     * This allows us to target for example both Full Time Positions and Full Time Position, or Interviewing and Interview
     */
    keywords.forEach((keyTerm) => {
      // add the term as it is
      addToDictionary(keyTerm);

      // if the term happens to end with an S, remove it and also add it to the dictionary
      const keywordWithSRemovedFromEnd =
        keyTerm.endsWith('s') && keyTerm.length > 1 ? keyTerm.replace(/s$/, '') : undefined;

      // if the term happens to end with ING, remove it and also add it to the dictionary
      const keywordRemovedIngFromEnd =
        keyTerm.endsWith('ing') && keyTerm.length > 3 ? keyTerm.replace(/ing$/, '') : undefined;

      if (keywordWithSRemovedFromEnd) {
        addToDictionary(keywordWithSRemovedFromEnd);
      }

      if (keywordRemovedIngFromEnd) {
        addToDictionary(keywordRemovedIngFromEnd);
      }
    });

    return acc;
  }, {} as DictionaryRecord);

  return additionalDictionary;
};
